import type { Note } from '@voice/shared';

const DB_NAME = 'voice-cache';
const DB_VERSION = 1;
const NOTES_STORE = 'notes';
const META_STORE = 'meta';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(NOTES_STORE)) {
        const store = db.createObjectStore(NOTES_STORE, { keyPath: 'id' });
        store.createIndex('user_id', 'user_id', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        const request = fn(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      })
  );
}

/** Replace all cached notes for a user with fresh data from Supabase. */
export async function setCachedNotes(userId: string, notes: Note[]): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([NOTES_STORE, META_STORE], 'readwrite');
    const store = transaction.objectStore(NOTES_STORE);
    const metaStore = transaction.objectStore(META_STORE);

    // Clear existing notes for this user, then add fresh ones
    const index = store.index('user_id');
    const cursorReq = index.openCursor(IDBKeyRange.only(userId));

    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        // All old notes cleared — insert new ones
        for (const note of notes) {
          store.put(note);
        }
        metaStore.put({ key: `lastSync:${userId}`, value: Date.now() });
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/** Get all cached notes for a user, sorted by created_at descending. */
export async function getCachedNotes(userId: string): Promise<Note[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(NOTES_STORE, 'readonly');
    const store = transaction.objectStore(NOTES_STORE);
    const index = store.index('user_id');
    const request = index.getAll(IDBKeyRange.only(userId));

    request.onsuccess = () => {
      const notes = (request.result as Note[]).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      resolve(notes);
    };
    request.onerror = () => reject(request.error);
  });
}

/** Get the timestamp of the last successful sync for a user. */
export async function getLastSyncTime(userId: string): Promise<number | null> {
  const result = await tx<{ key: string; value: number } | undefined>(
    META_STORE,
    'readonly',
    (store) => store.get(`lastSync:${userId}`)
  );
  return result?.value ?? null;
}
